/**
 * Текущая (рыночная) цена одной бумаги из портфеля клиента.
 *
 * Расчёт был написан дважды — на главной и на странице портфолио — и разошёлся:
 *  - облигации на портфолио оценивались по `ask`, а на главной по середине
 *    спреда, то есть на половину спреда × количество бумаг;
 *  - крипта на главной вообще не попадала в рыночную ветку (её не было в списке
 *    типов) и оставалась по цене покупки — P&L по ней всегда был нулевой;
 *  - главная дополнительно резала цену до 2 знаков, из-за чего дешёвые бумаги
 *    схлопывались в 0.00 и подменялись средней ценой входа.
 * Поэтому сумма портфеля по одной и той же валюте на двух экранах не совпадала.
 *
 * Канон — версия страницы портфолио, она здесь и лежит; оба экрана зовут её.
 *
 * Модуль намеренно без зависимостей от стора и сокета: котировки приходят
 * параметром картой `EXCHANGE~SYMBOL -> {bid, ask}` — так расчёт покрывается
 * тестами, не поднимая socket.io и marketStore.
 */
import { AssetType } from "static/types";

/** Живые котировки из RT-сокета. `null` — подписка есть, тика ещё не было. */
export type RtPriceMap = Record<string, null | { bid: number; ask: number }>;

/**
 * Структурный минимум от `IUserBrokerageAccount` — ровно те поля, которые
 * читает расчёт. Позволяет звать функцию и из тестов голым объектом.
 */
export interface PositionForPricing {
  /** Средневзвешенная цена входа по позиции. */
  price: number;
  asset: {
    symbol: string;
    type: number;
    /** Последняя цена закрытия из справочника активов. */
    price: number;
    ask: number;
    bid: number;
    data: any;
    exchange: null | { symbol: string };
  };
}

/**
 * Номинал облигации. `data` у актива может прийти пустым — бэкенд в таких
 * случаях тоже берёт 100 (market.service.ts: `?.nominal || 100`), а без
 * `?.` расчёт падал бы прямо в рендере.
 */
const nominalOf = (data: any): number => data?.nominal ?? 100;

export function getPositionMarketPrice(
  account: PositionForPricing,
  rtPrices: RtPriceMap,
): number {
  const { asset, price: tradePrice } = account;
  const {
    exchange: ex,
    symbol,
    ask,
    bid,
    data,
    type,
    price: lastPrice,
  } = asset;

  const rtPrice = rtPrices[ex ? `${ex.symbol}~${symbol}` : symbol];
  const price = +tradePrice.toFixed(4);
  const closePrice = lastPrice ? +lastPrice.toFixed(4) : price;

  if (
    [
      AssetType.Stock,
      AssetType.ETF,
      AssetType.Fond,
      AssetType.Crypto,
    ].includes(type)
  ) {
    const midPrice = rtPrice
      ? +((rtPrice.bid + rtPrice.ask) / 2).toFixed(2)
      : null;
    return midPrice || closePrice || price;
  }

  if ([AssetType.Bond, AssetType.EuroBond].includes(type)) {
    // ask и asset.price у облигации — котировки В ПРОЦЕНТАХ ОТ НОМИНАЛА
    // (bond.service.ts: `ask: b.ask, price: b.close_price`), поэтому их и
    // делят на 100. А вот цена входа — уже ДЕНЬГИ за бумагу
    // (orders.processor.ts: `last_px * nominal / 100`). Портфолио уходило в
    // фолбэк на неё через closePrice и по бумаге без котировок показывало
    // `цена_входа / 100 * номинал` — для евробонда с номиналом 1000 это
    // десятикратная стоимость позиции. Фолбэк оставляем только на котировки;
    // когда их нет вообще — ниже вернётся цена входа как есть.
    const bondQuote = ask || (lastPrice ? +lastPrice.toFixed(4) : 0);
    const quote =
      parseFloat(((bondQuote / 100) * nominalOf(data)).toFixed(2)) || 0;
    return quote || price;
  }

  if (type === AssetType.StructuredProduct) {
    const quote =
      parseFloat((((bid + ask) / 2 / 100) * nominalOf(data)).toFixed(2)) || 0;
    return quote || price;
  }

  // Опционы, фьючерсы и всё остальное живой оценки не имеют — цена входа.
  return price;
}

/**
 * НКД по позиции: накоплено на сегодня за все бумаги.
 *
 * `getPositionMarketPrice` возвращает ЧИСТУЮ цену — без накопленного купонного
 * дохода, поэтому ни стоимость позиции, ни её P&L его не содержат. Клиент же
 * платил за облигацию вместе с НКД, и без этой величины его списание не сходится
 * с карточкой (купил по 100 000 + 310 НКД, а видит только 100 000).
 *
 * `asset.data.ai` — НКД ЗА ОДНУ бумагу в валюте актива, а не проценты
 * (бэкенд: bond-ai.ts → bonds.ai → assets.data.ai). Количество передаётся
 * явно: карточка считает стоимость от своего `count` (с заблокированными), и
 * НКД обязан браться от того же числа, иначе строки не сойдутся.
 *
 * Не облигация, бескупонная бумага или отсутствующий `ai` дают 0 — строку НКД
 * в этом случае не рисуем.
 */
export function getPositionAccrued(
  asset: { type: number; data: any },
  quantity: number,
): number {
  if (![AssetType.Bond, AssetType.EuroBond].includes(asset.type)) return 0;

  const ai = Number(asset.data?.ai);
  if (!Number.isFinite(ai) || ai <= 0) return 0;

  return +(ai * quantity).toFixed(2);
}
